import Joi from 'joi';
import schemas from 'components/forms/fields/definedSchemas';

const fields = {
    official_letterhead: {
        ...schemas.boolRequired,
        section: 'Signature',
        label: 'Is your Transcript of Sea Service on official letterhead?',
    },
    document_signed: {
        ...schemas.boolRequired,
        section: 'Signature',
        label: 'Is your Transcript of Sea Service signed by an authorized representative?',
    },
    date_document_signed: {
        ...schemas.dateRequiredMaxToday,
        section: 'Signature',
        label: 'What is the date on the TOSS?',
    },
    ssl_type: {
        ...schemas.textRequired,
        section: 'Signature',
        initialValue: 'armytoss',
        hidden: true,
    },
    vessel_name: {
        ...schemas.textRequired,
        section: 'Vessel',
        label: 'Vessel Name',
    },
    imo_number: {
        ...schemas.imoRequired,
        section: 'Vessel',
        label: 'IMO',
    },
    vessel_type: {
        ...schemas.radioRequired,
        section: 'Vessel',
        label: 'Type of Vessel',
        options: {
            Motor: 'Motor',
            Towing: 'Towing',
            'Tank Ship or Barge': 'Tank Ship or Barge',
            'High Speed Craft': 'High Speed Craft',
        },
    },
    tankship_or_barge_type: {
        ...schemas.radioRequired,
        section: 'Vessel',
        label: 'Tankship or Barge Type',
        options: {
            'Tank Ship Liquified Gasses': 'Tank Ship (Liquified Gasses)',
            'Tank Ship Dangerous Liquids': 'Tank Ship (Dangerous Liquids)',
            'Tank Ship Chemical': 'Tank Ship (Chemical)',
            'Tank Barge Liquified Gasses': 'Tank Barge (Liquified Gasses)',
            'Tank Barge Dangerous Liquids': 'Tank Barge (Dangerous Liquids)',
            'Tank Barge Chemical': 'Tank Barge (Chemical)',
        },
        validation: Joi.string()
            .max(100)
            .when(Joi.ref('vessel_type'), {
                is: 'Tank Ship or Barge',
                then: Joi.string().required(),
                otherwise: Joi.string().allow(''),
            }),
    },
    tonnage: {
        ...schemas.floatRequired,
        section: 'Vessel',
        label: 'Tonnage',
    },
    tonnage_type: {
        ...schemas.tonnageTypeRequiredToss,
        section: 'Vessel',
        label: 'What type of tonnage is used on this form?',
    },
    length_tonnage: {
        ...schemas.floatRequired,
        section: 'Vessel',
        label: 'Length',
    },

    engine_horsepower: {
        ...schemas.floatRequired,
        section: 'Vessel',
        label: 'Horsepower',
    },
    date_reported: {
        ...schemas.dateRequiredMaxToday,
        section: 'ShipmentAndDischarge',
        label: 'Date Reported',
    },
    date_detached: {
        ...schemas.dateRequiredMaxToday,
        section: 'ShipmentAndDischarge',
        label: 'Date Detached',
        validation: schemas.dateRequiredMaxToday.validation
            .min(Joi.ref('date_reported'))
            .messages({
                'date.min': "Date detached can't be before date reported.",
            }),
    },
    department: {
        ...schemas.radioRequired,
        section: 'ShipmentAndDischarge',
        label: 'Did you serve in the Deck Department or the Engine Department?',
        options: {Deck: 'Deck', Engine: 'Engine'},
    },

    deck_position_served: {
        ...schemas.selectRequired,
        section: 'ShipmentAndDischarge',
        label: 'Deck Position Served',
        validation: Joi.string()
            .max(100)
            .when(Joi.ref('department'), {
                is: 'Deck',
                then: Joi.string().required(),
                otherwise: Joi.string().allow(''),
            }),
        options: {
            Master: 'Master',
            'Chief Mate / 1st Mate': 'Chief Mate / 1st Mate',
            '2nd Mate': '2nd Mate',
            '3rd Mate': '3rd Mate',
            Mate: 'Mate',
            'Master of Towing': 'Master of Towing',
            'Mate Pilot of Towing': 'Mate Pilot of Towing',
            'Apprentice Mate (Steersman)': 'Apprentice Mate (Steersman)',
            'Mate (Trainee)': 'Mate (Trainee)',
            'First Class Pilot': 'First Class Pilot',
            'Apprentice Pilot': 'Apprentice Pilot',
            'OUPV Operator': 'OUPV Operator',
            'Able Seaman': 'Able Seaman',
            Boatswain: 'Boatswain',
            Quartermaster: 'Quartermaster',
            'Deck Hand': 'Deck Hand',
            'Crew Member': 'Crew Member',
            'Entry Level': 'Entry Level',
        },
    },
    engine_position_served: {
        ...schemas.selectRequired,
        section: 'ShipmentAndDischarge',
        label: 'Engine Position Served',
        validation: Joi.string()
            .max(100)
            .when(Joi.ref('department'), {
                is: 'Engine',
                then: Joi.string().required(),
                otherwise: Joi.string().allow(''),
            }),
        options: {
            'Chief Engineer': 'Chief Engineer',
            '1st Assistant Engineer': '1st Assistant Engineer',
            '2nd Assistant Engineer': '2nd Assistant Engineer',
            '3rd Assistant Engineer': '3rd Assistant Engineer',
            'Assistant Engineer': 'Assistant Engineer',
            'Designated Duty Engineer': 'Designated Duty Engineer',
            Engineer: 'Engineer',
            'Junior Unlicensed Engineer': 'Junior Unlicensed Engineer',
            'Junior Engineer': 'Junior Engineer',
            'Qualified Member of the Engine Department (QMED)':
                'Qualified Member of the Engine Department (QMED)',
            'Electrician-Refrigerating Engineer':
                'Electrician-Refrigerating Engineer',
            Oiler: 'Oiler',
            'Fireman-Watertender': 'Fireman-Watertender',
            'Machinist-Pumpman': 'Machinist-Pumpman',
            Wiper: 'Wiper',
            'Crew Member': 'Crew Member',
            'Entry Level': 'Entry Level',
        },
    },

    deck_supervised: {
        ...schemas.boolOptional,
        section: 'ShipmentAndDischarge',
        label: 'Did you perform Bridge Watchkeeping duties under supervision during any portion of this time?',
        initialValue: null,
    },
    deck_performed_under_supervision_days: {
        ...schemas.integerOptional,
        section: 'ShipmentAndDischarge',
        label: 'Number of days of Supervised Bridge Watchkeeping',
        validation: Joi.number()
            .integer()
            .min(0)
            .when(Joi.ref('deck_supervised'), {
                is: true,
                then: Joi.number().required().messages({
                    'any.required': 'Please enter the number of days.',
                }),
                otherwise: Joi.number().allow(null),
            }),
        initialValue: null,
    },
    engine_supervised: {
        ...schemas.boolOptional,
        section: 'ShipmentAndDischarge',
        label: 'Did you perform Engine Watchkeeping duties under supervision during any portion of this time?',
        initialValue: null,
    },
    engine_performed_under_supervision_days: {
        ...schemas.integerOptional,
        section: 'ShipmentAndDischarge',
        label: 'Number of Days of Supervised Engine Watchkeeping',
        validation: Joi.number()
            .integer()
            .min(0)
            .when(Joi.ref('engine_supervised'), {
                is: true,
                then: Joi.number().required().messages({
                    'any.required': 'Please enter the number of days.',
                }),
                otherwise: Joi.number().allow(null),
            }),
        initialValue: null,
    },
    towing_training: {
        ...schemas.boolOptional,
        section: 'ShipmentAndDischarge',
        label: 'Did you perform Towing Training and Observation during any portion of this time?',
        initialValue: null,
    },
    towing_training_and_observation_days: {
        ...schemas.integerOptional,
        section: 'ShipmentAndDischarge',
        label: 'Number of Towing Training and Observation Days',
        validation: Joi.number()
            .integer()
            .min(0)
            .when(Joi.ref('towing_training'), {
                is: true,
                then: Joi.number().required().messages({
                    'any.required': 'Please enter the number of days.',
                }),
                otherwise: Joi.number().allow(null),
            }),
        initialValue: null,
    },
    watertype: {
        ...schemas.selectRequired,
        section: 'ShipmentAndDischarge',
        label: 'Water Type',
        validation: Joi.string().max(100).required(),
        options: {
            Oceans: 'Oceans',
            'Near Coastal': 'Near Coastal',
            Inland: 'Inland',
            'Great Lakes': 'Great Lakes',
            'Western Rivers': 'Western Rivers',
        },
    },
    also_polar: {
        ...schemas.boolOptional,
        section: 'ShipmentAndDischarge',
        label: 'Does this sea service entry documented on your letter indicate Polar, Arctic, or Antarctic service?',
    },
};

export default fields;
